import { handleLogout, handleSoftLogout, sessionExpiration } from "@/helpers/auth";

class ApiError extends Error {
  type: string;
  detail?: Array<string>;
  status?: number;
  code?: string;
  constructor(message: string, detail?: string[], status?: number, code?: string) {
    super(message);
    this.type = "ApiError";
    this.detail = detail;
    this.status = status;
    this.code = code;
  }
}

type ApiResponseError = {
  status: number;
  message: string;
  code:string;
  detail: Array<string>;
};

/**
 * @description
 * Handles JSON responses from the API, checking if it's OK and parsing the JSON.
 * Also handles the error case, throwing an ApiError.
 * @param response - The response from the API.
 * @param fallbackMessage - The fallback message to use if the response is not OK.
 * @returns The parsed JSON response.
 */

export async function apiResponseHandler<T>(
  response: Response,
  fallbackMessage?: string,
): Promise<T> {
  if (!response.ok) {
    await handleApiError(response, fallbackMessage);
  }
  try {
    return await response.json();
  }
  catch { //Fallback for endpoints that have no return
    return null;
  }
}

/**
 * @description
 * Handles file responses from the API, checking if it's OK and returning a Blob.
 * Also handles the error case, throwing an ApiError.
 * @param response - The response from the API.
 * @param fallbackMessage - The fallback message to use if the response is not OK.
 * @returns The file response.
 */

export async function apiFileResponseHandler(response: Response, fallbackMessage?: string): Promise<Blob> {
  if (!response.ok) {
    await handleApiError(response, fallbackMessage);
  }
  return response.blob();
}

async function handleApiError(response: Response, fallbackMessage?: string): Promise<never> {
  if (response.status === 401) {
    handleUnauthorizedError();
  }
  let errorMessage = fallbackMessage ?? "Erro no servidor";
  try {
    const responseBody: ApiResponseError = await response.json();
    if (responseBody.message) {
      throw new ApiError(responseBody.message, responseBody.detail, responseBody.status, responseBody.code);
    }
  } catch (e) {
    if (e instanceof ApiError) throw e; // re-throw ApiError as-is
    // JSON parsing failed, fall through to fallback
  }
  throw new Error(errorMessage);
}

/**
 * @description
 * Handles the case of 401 Unauthorized error.
 * It checks if the session is expired and, if so, logs out the user.
 */
function handleUnauthorizedError() {
  if (sessionExpiration.isExpired()) {
    handleSoftLogout();
    throw new ApiError("Sessão expirada", "", 401);
  }
}
